import { z } from 'zod';
import { productVariantExchangeResponse } from '../customer/schema/variantExchange.js';
import { claimAddress, verifyAddressResponse } from '../schema/address.js';
import { claim, claimCreate, claimCustomerPayment, claimFinalizePayload, claimList, claimListCount, claimListItem, claimNeedsMoreInfoPayload, claimNotifyByEmail, claimUpdateCustomerInfo, claimUpdateFees, tag, } from '../schema/claim.js';
import { approvedFinalizationLineItem, claimLineItem, claimLineItemFeeUpdate, claimLineItemInspection, claimLineItemInspectionCreate, claimLineItemUpdateResolution, } from '../schema/claimLineItem.js';
import { claimReason, claimReasonCreate, claimReasonDetailUpdate, claimReasonGroup, claimReasonGroupCreate, claimReasonGroupUpdate, claimReasonUpdate, } from '../schema/claimReasons.js';
import { crewOrder, orderListSchema, } from '../schema/order.js';
import { claimTimeline, claimTimelineEvent, timelineUser, } from '../schema/timeline.js';
import { orderShipments, } from '../schema/tracking.js';
import { PrimaryEntity } from '../../enums/crew.js';
import { claimOrderLineage } from '../schema/claimOrderLineage.js';
import { claimRiskAssessment } from '../schema/claimRiskAssessment.js';
import { customField, customFieldCreate, customFieldUpdate, } from '../schema/customField.js';
import { platformMetafield, storeMetafield, storeMetafieldCreate, } from '../schema/storeMetafield.js';
import { conciergeSearchFilterReq, pageInfo, reportDefinitionByEntity, reportDefinitionCreateRequest, reportDefinitionResponse, reportDefinitionUpdateRequest, reportFiltersByEntity, reportInlineRunRequest, searchFilterReq, storeUiView, viewDateValue, } from '../schema/filters.js';
import { storeIdentityGrant, storeIdentityGrantCreate, storeIdentityGrantWithClientSecret, } from '../schema/identityGrant.js';
import { userMcpIdentityGrant, userMcpIdentityGrants, } from '../schema/mcpIdentityGrant.js';
import { claimInspectionSettings, inspectionTemplate, } from '../schema/inspection.js';
import { registration, registrationList, registrationUpdate, } from '../schema/registration.js';
import { returnLocation, returnLocationCreate, } from '../schema/returnLocation.js';
import { shipmentAdminCreate, shipmentAdminQuote, shipmentAdminQuoteAndBuy, shipmentAdminQuoteResponse, } from '../schema/shipment.js';
import { shippingClaimCreateMerchant, shippingClaimFinalize, shippingClaimInventoryLevels, shippingClaimLineItemAdd, shippingClaimList, shippingClaimMerchant, shippingClaimMessages, shippingClaimUpdate, shippingClaimUpdateCustomerInfo, } from '../schema/shippingClaim/shippingClaim.js';
import { shippingClaimRiskAssessment } from '../schema/shippingClaimRiskAssessment.js';
import { signedUrl } from '../schema/signedUrl.js';
import { storeAlert, storeAlertCreate, storeAlertUpdate, } from '../schema/storeAlert.js';
import { amountOffOrderRaw, amountOffProductRaw, discountDefinition, storeDiscountDefinition, } from '../schema/storeDiscountDefinition.js';
import { productCollection, productTag, productType, storeProduct, storeProductDetail, storeProductGroup, storeProductGroupCreate, storeProductGroupMembership, storeProductImport, storeProductImportAccepted, storeProductList, storeProductMinimum, storeProductRegistrationChannels, storeProductUpdate, } from '../schema/storeProduct.js';
import { arrayFactData, storeRule, storeRuleCreate, } from '../schema/storeRule.js';
import { apiClientResp } from './error.js';
import { configWebhook, configWebhookCreate, configWebhookDelivery, configWebhookDeliveryDefaultLookbackDays, configWebhookDeliveryFilters, configWebhookDeliveryMaxLookbackDays, ConfigWebhookDeliveryResponseBucket as ConfigWebhookDeliveryResponseBucketEnum, configWebhookDeliverySearchReq, configWebhookDeliverySearchResp, ConfigWebhookDeliveryStatus as ConfigWebhookDeliveryStatusEnum, configWebhookEvent, configWebhookEventCreate, configWebhookEventUpdate, ConfigWebhookTopic as ConfigWebhookTopicEnum, configWebhookUpdate, } from './schema/configWebhook.js';
import { invoice } from './schema/invoices.js';
import { merchantOnboard } from './schema/merchantOnboard.js';
import { appSubscription, blueBoxConfigForm, blueBoxUpdate, easyPostUpdate, globalEConfigUpdate, happyReturnsConfigUpdate, klaviyoTestEventRequest, klaviyoTestEventResponse, klaviyoUpdate, kustomerConfigForm, kustomerConfigUpdate, logiwaConfigUpdate, masonHubConfigForm, masonHubUpdate, rechargeConfigForm, rechargeUpdate, sendLaneConfigForm, sendlaneUpdate, settingsConfig, settingsIntegration, settingsIntegrationUpdate, settingsTheme, shipBobConfigForm, shipBobUpdate, shipHeroConfigForm, shipHeroUpdate, shippingProtection, shippingProvider, shippingProviderForm, shippingProviderRefresh, shippingProviderUpdate, skioConfigForm, skioConfigUpdate, statusConfigUpdate, twoBoxesConfigForm, twoBoxesConfigUpdate, vesylUpdate, xbFulfillmentConfigForm, xbFulfillmentUpdate, } from './schema/settings.js';
import { conciergeCustomer, storeCustomer } from './schema/storeCustomer.js';
import { protectionRate, protectionRateCreate, Carrier as StoreShipRateCarrier, CarrierService as StoreShipRateCarrierService, } from './schema/storeShipRate.js';
import { userAccessCodeStore } from './schema/storeWithUsers.js';
import { storeUser, storeUserCreate, storeUserSignUp, storeUserUpdate, userSignUp, userSignUpAndOnboard, userWithStoreUserRoles, } from './schema/user.js';
/**
 * CrewUi types for the crew-merchant-ui :)
 * Utility types to be used in conjunction with the relevant API endpoint types.
 */
// oxlint-disable-next-line @typescript-eslint/no-namespace
export var CrewMerchantUi;
(function (CrewMerchantUi) {
    // ? we still want to be able to support that do not have a product / variant ID from Shopify, currently this is an error for us
    // ? urls being used by the API to redirect to the merchant-ui
    CrewMerchantUi.frontEndUrlPaths = [
        `:storeId/settings/integrations`, // ? used by the API to redirect to the merchant-ui after app subscription generation + stripe subscription
        ':storeId/claims/:claimType/:claimId', // ? used by the API to redirect to a specific claim in the merchant-ui
        ':storeId/registrations/:registrationId', // ? used by the API to redirect to a specific registration in the merchant-ui
        ':storeId/settings/email',
        ':storeId/settings/shipping-policies',
        ':storeId/claims/shipping/:shippingClaimId', // ? deep link to a specific shipping (GSP) claim in the merchant-ui
        ':storeId/orders/lookup/:idFromPlatform', // ? deep link to a specific order in the merchant-ui
        ':storeId/settings/automations/edit/:storeRuleId', // ? deep link to a specific automation in the merchant-ui
    ];
    CrewMerchantUi.shippingClaimCreatedTimelineEventTitle = 'Created shipping claim.';
    CrewMerchantUi.ORDER_PREFIX_DEFAULT = {
        exchange: '#C-EX-',
        returnReplacement: '#C-RO-',
        warrantyReplacement: '#C-WR-',
        giftCard: '#C-GC-',
    };
    // Shared Constants
    CrewMerchantUi.CREW_DISCOUNT_CODE = 'crew-discount';
    CrewMerchantUi.MERCHANT_APP_JWT_QUERY_KEY = 'accessCode';
    CrewMerchantUi.invoiceSchema = invoice;
    CrewMerchantUi.storeIdentityGrantSchema = storeIdentityGrant;
    CrewMerchantUi.storeIdentityGrantCreateSchema = storeIdentityGrantCreate;
    CrewMerchantUi.storeIdentityGrantWithClientSecretSchema = storeIdentityGrantWithClientSecret;
    CrewMerchantUi.userMcpIdentityGrantSchema = userMcpIdentityGrant;
    CrewMerchantUi.userMcpIdentityGrantsSchema = userMcpIdentityGrants;
    // ----------- protection rates ------------
    CrewMerchantUi.Carrier = StoreShipRateCarrier;
    CrewMerchantUi.CarrierService = StoreShipRateCarrierService;
    CrewMerchantUi.protectionRateSchema = protectionRate;
    CrewMerchantUi.protectionRateUpdateSchema = protectionRate;
    CrewMerchantUi.protectionRateCreateSchema = protectionRateCreate;
    CrewMerchantUi.storeCustomerSchema = storeCustomer;
    CrewMerchantUi.conciergeCustomerSchema = conciergeCustomer;
    CrewMerchantUi.userAccessCodeSchema = userAccessCodeStore;
    // ----------  App Subscription  ----------
    CrewMerchantUi.appSubscriptionSchema = appSubscription;
    CrewMerchantUi.userWithStoreUserRolesSchema = userWithStoreUserRoles;
    CrewMerchantUi.storeUserSchema = storeUser;
    CrewMerchantUi.storeUserCreateSchema = storeUserCreate;
    CrewMerchantUi.storeUserUpdateSchema = storeUserUpdate;
    CrewMerchantUi.signedUrlSchema = signedUrl;
    CrewMerchantUi.returnLocationSchema = returnLocation;
    CrewMerchantUi.returnLocationCreateSchema = returnLocationCreate;
    // ---------- Store InspectionSettings   ----------
    CrewMerchantUi.inspectionTemplateSchema = inspectionTemplate;
    CrewMerchantUi.claimInspectionSettingsSchema = claimInspectionSettings;
    CrewMerchantUi.claimFinalizePayloadSchema = claimFinalizePayload;
    CrewMerchantUi.ClaimFinalizeApprovedLineItemSchema = approvedFinalizationLineItem;
    CrewMerchantUi.claimTimelineSchema = claimTimeline;
    CrewMerchantUi.claimTimelineEventSchema = claimTimelineEvent;
    CrewMerchantUi.timelineSchema = claimTimeline;
    CrewMerchantUi.timelineEventSchema = claimTimelineEvent;
    CrewMerchantUi.timelineUserSchema = timelineUser;
    CrewMerchantUi.claimNotifyByEmailSchema = claimNotifyByEmail;
    CrewMerchantUi.claimNeedsMoreInfoSchema = claimNeedsMoreInfoPayload;
    CrewMerchantUi.claimReasonSchema = claimReason;
    CrewMerchantUi.claimReasonCreateSchema = claimReasonCreate;
    CrewMerchantUi.claimReasonUpdateSchema = claimReasonUpdate;
    CrewMerchantUi.claimReasonDetailUpdateSchema = claimReasonDetailUpdate;
    CrewMerchantUi.claimReasonGroupSchema = claimReasonGroup;
    CrewMerchantUi.claimReasonGroupUpdateSchema = claimReasonGroupUpdate;
    CrewMerchantUi.claimReasonGroupCreateSchema = claimReasonGroupCreate;
    CrewMerchantUi.claimSchema = claim;
    CrewMerchantUi.claimUpdateCustomerInfoSchema = claimUpdateCustomerInfo;
    CrewMerchantUi.claimUpdateRefundHandlingFeeSchema = claimUpdateFees;
    CrewMerchantUi.claimTagSchema = tag;
    CrewMerchantUi.claimTagCreateSchema = tag.omit({ id: true });
    CrewMerchantUi.shippingClaimTagSchema = tag;
    CrewMerchantUi.shippingClaimTagCreateSchema = tag.omit({ id: true });
    CrewMerchantUi.registrationTagSchema = tag;
    CrewMerchantUi.registrationTagCreateSchema = tag.omit({ id: true });
    CrewMerchantUi.claimMinimumDataSchema = claimListItem;
    CrewMerchantUi.claimListSchema = claimList;
    CrewMerchantUi.claimListCountSchema = claimListCount;
    CrewMerchantUi.claimListItemSchema = claimListItem;
    CrewMerchantUi.claimLineItemSchema = claimLineItem;
    CrewMerchantUi.claimShipmentQuoteAndBuySchema = shipmentAdminQuoteAndBuy;
    CrewMerchantUi.claimReturnShipmentCreateSchema = shipmentAdminCreate;
    CrewMerchantUi.claimReturnShipmentQuoteSchema = shipmentAdminQuote;
    CrewMerchantUi.claimReturnShipmentQuoteResponse = shipmentAdminQuoteResponse;
    CrewMerchantUi.claimLineItemInspectionSchema = claimLineItemInspection;
    CrewMerchantUi.claimLineItemInspectionCreateSchema = claimLineItemInspectionCreate;
    CrewMerchantUi.claimAddressSchema = claimAddress;
    CrewMerchantUi.verifyAddressResponseSchema = verifyAddressResponse;
    CrewMerchantUi.claimCustomerPaymentSchema = claimCustomerPayment;
    CrewMerchantUi.settingsConfigSchema = settingsConfig;
    CrewMerchantUi.orderShipmentsSchema = orderShipments;
    CrewMerchantUi.settingsThemeSchema = settingsTheme;
    CrewMerchantUi.settingsIntegrationSchema = settingsIntegration;
    CrewMerchantUi.shipProtectSettingsSchema = shippingProtection;
    CrewMerchantUi.blueBoxUpdateSchema = blueBoxUpdate;
    CrewMerchantUi.masonHubUpdateSchema = masonHubUpdate;
    CrewMerchantUi.shipBobUpdateSchema = shipBobUpdate;
    CrewMerchantUi.shipHeroUpdateSchema = shipHeroUpdate;
    CrewMerchantUi.skioConfigUpdateSchema = skioConfigUpdate;
    CrewMerchantUi.kustomerConfigUpdateSchema = kustomerConfigUpdate;
    CrewMerchantUi.xBFulfillmentUpdateSchema = xbFulfillmentUpdate;
    CrewMerchantUi.klaviyoConfigUpdateSchema = klaviyoUpdate;
    CrewMerchantUi.klaviyoTestEventSchema = klaviyoTestEventRequest;
    CrewMerchantUi.klaviyoTestEventResponseSchema = klaviyoTestEventResponse;
    CrewMerchantUi.sendlaneUpdateSchema = sendlaneUpdate;
    CrewMerchantUi.shippingProviderUpdateSchema = shippingProviderUpdate;
    CrewMerchantUi.shippingProviderSchema = shippingProvider;
    CrewMerchantUi.shippingProviderRefreshSchema = shippingProviderRefresh;
    CrewMerchantUi.vesylUpdateSchema = vesylUpdate;
    CrewMerchantUi.rechargeUpdateSchema = rechargeUpdate;
    CrewMerchantUi.easyPostUpdateSchema = easyPostUpdate;
    CrewMerchantUi.statusConfigUpdateSchema = statusConfigUpdate;
    CrewMerchantUi.logiwaUpdateSchema = logiwaConfigUpdate;
    CrewMerchantUi.twoBoxesUpdateSchema = twoBoxesConfigUpdate;
    CrewMerchantUi.globalEUpdateSchema = globalEConfigUpdate;
    CrewMerchantUi.happyReturnsUpdateSchema = happyReturnsConfigUpdate;
    CrewMerchantUi.settingsIntegrationUpdateSchema = settingsIntegrationUpdate;
    CrewMerchantUi.shipHeroConfigFormSchema = shipHeroConfigForm;
    CrewMerchantUi.shipBobConfigFormSchema = shipBobConfigForm;
    CrewMerchantUi.blueBoxConfigFormSchema = blueBoxConfigForm;
    CrewMerchantUi.masonHubConfigFormSchema = masonHubConfigForm;
    CrewMerchantUi.xbFulfillmentConfigFormSchema = xbFulfillmentConfigForm;
    CrewMerchantUi.sendLaneConfigFormSchema = sendLaneConfigForm;
    CrewMerchantUi.skioConfigFormSchema = skioConfigForm;
    CrewMerchantUi.kustomerConfigFormSchema = kustomerConfigForm;
    CrewMerchantUi.twoBoxesConfigFormSchema = twoBoxesConfigForm;
    CrewMerchantUi.rechargeConfigFormSchema = rechargeConfigForm;
    CrewMerchantUi.shippingProviderFormSchema = shippingProviderForm;
    CrewMerchantUi.ConfigWebhookTopic = ConfigWebhookTopicEnum;
    CrewMerchantUi.ConfigWebhookDeliveryStatus = ConfigWebhookDeliveryStatusEnum;
    CrewMerchantUi.configWebhookSchema = configWebhook;
    CrewMerchantUi.configWebhookCreateSchema = configWebhookCreate;
    CrewMerchantUi.configWebhookUpdateSchema = configWebhookUpdate;
    CrewMerchantUi.configWebhookEventSchema = configWebhookEvent;
    CrewMerchantUi.configWebhookEventCreateSchema = configWebhookEventCreate;
    CrewMerchantUi.configWebhookEventUpdateSchema = configWebhookEventUpdate;
    CrewMerchantUi.configWebhookDeliverySchema = configWebhookDelivery;
    CrewMerchantUi.ConfigWebhookDeliveryResponseBucket = ConfigWebhookDeliveryResponseBucketEnum;
    CrewMerchantUi.configWebhookDeliveryFiltersSchema = configWebhookDeliveryFilters;
    CrewMerchantUi.configWebhookDeliverySearchReqSchema = configWebhookDeliverySearchReq;
    CrewMerchantUi.configWebhookDeliverySearchRespSchema = configWebhookDeliverySearchResp;
    CrewMerchantUi.CONFIG_WEBHOOK_DELIVERY_MAX_LOOKBACK_DAYS = configWebhookDeliveryMaxLookbackDays;
    CrewMerchantUi.CONFIG_WEBHOOK_DELIVERY_DEFAULT_LOOKBACK_DAYS = configWebhookDeliveryDefaultLookbackDays;
    CrewMerchantUi.storeProductSchema = storeProduct;
    CrewMerchantUi.storeProductListSchema = storeProductList;
    CrewMerchantUi.storeProductDetailSchema = storeProductDetail;
    CrewMerchantUi.storeProductTypeSchema = productType;
    CrewMerchantUi.storeProductTagSchema = productTag;
    CrewMerchantUi.storeProductCollectionSchema = productCollection;
    CrewMerchantUi.storeProductGroupSchema = storeProductGroup;
    CrewMerchantUi.storeProductGroupCreateSchema = storeProductGroupCreate;
    CrewMerchantUi.storeProductImportSchema = storeProductImport;
    CrewMerchantUi.storeProductImportAcceptedSchema = storeProductImportAccepted;
    CrewMerchantUi.storeProductUpdateSchema = storeProductUpdate;
    CrewMerchantUi.storeProductGroupMembershipSchema = storeProductGroupMembership;
    CrewMerchantUi.storeProductRegistrationChannelsSchema = storeProductRegistrationChannels;
    CrewMerchantUi.storeProductsForGroupSchema = storeProductMinimum;
    CrewMerchantUi.storeProductGroupUpdateSchema = storeProductGroup;
    CrewMerchantUi.apiClientRespSchema = apiClientResp;
    CrewMerchantUi.merchantOnboardSchema = merchantOnboard;
    CrewMerchantUi.arrayFactDataSchema = arrayFactData;
    CrewMerchantUi.storeRuleSchema = storeRule;
    CrewMerchantUi.storeRuleUpdateSchema = storeRule;
    CrewMerchantUi.storeRuleCreateSchema = storeRuleCreate;
    CrewMerchantUi.customFieldSchema = customField;
    CrewMerchantUi.customFieldCreateSchema = customFieldCreate;
    CrewMerchantUi.customFieldUpdateSchema = customFieldUpdate;
    CrewMerchantUi.storeMetafieldSchema = storeMetafield;
    CrewMerchantUi.platformMetafieldSchema = platformMetafield;
    CrewMerchantUi.storeMetafieldCreateSchema = storeMetafieldCreate;
    CrewMerchantUi.claimLineItemUpdateSchema = claimLineItemUpdateResolution;
    CrewMerchantUi.claimLineItemFeeUpdateSchema = claimLineItemFeeUpdate;
    CrewMerchantUi.crewOrderSchema = crewOrder;
    CrewMerchantUi.claimCreateSchema = claimCreate;
    CrewMerchantUi.variantExchangeResponseSchema = productVariantExchangeResponse;
    CrewMerchantUi.shippingClaimSchema = shippingClaimMerchant;
    CrewMerchantUi.shippingClaimMessagesSchema = shippingClaimMessages;
    CrewMerchantUi.shippingClaimListSchema = shippingClaimList;
    CrewMerchantUi.shippingClaimFinalizeSchema = shippingClaimFinalize;
    CrewMerchantUi.shippingClaimCreateSchema = shippingClaimCreateMerchant;
    CrewMerchantUi.shippingClaimLineItemAddSchema = shippingClaimLineItemAdd;
    CrewMerchantUi.shippingClaimInventoryLevelsSchema = shippingClaimInventoryLevels;
    CrewMerchantUi.shippingClaimUpdateSchema = shippingClaimUpdate;
    CrewMerchantUi.shippingClaimUpdateCustomerInfoSchema = shippingClaimUpdateCustomerInfo;
    CrewMerchantUi.storeUserSignupCreateSchema = storeUserSignUp;
    CrewMerchantUi.storeUserSignUpSchema = userSignUp;
    CrewMerchantUi.storeUserSignUpAndOnboardSchema = userSignUpAndOnboard;
    CrewMerchantUi.registrationSchema = registration;
    CrewMerchantUi.registrationUpdateSchema = registrationUpdate;
    CrewMerchantUi.registrationListSchema = registrationList;
    CrewMerchantUi.searchReqSchema = searchFilterReq;
    CrewMerchantUi.conciergeSearchReqSchema = conciergeSearchFilterReq;
    CrewMerchantUi.MAX_SEARCH_TERM_LENGTH = 100;
    CrewMerchantUi.searchRespSchema = z.discriminatedUnion('kind', [
        z.object({
            data: CrewMerchantUi.shippingClaimListSchema,
            kind: z.literal(PrimaryEntity.Shipping),
            pageInfo,
        }),
        z.object({
            data: orderListSchema,
            kind: z.literal(PrimaryEntity.Order),
            pageInfo,
        }),
        z.object({
            data: CrewMerchantUi.claimListSchema,
            kind: z.literal(PrimaryEntity.Return),
            pageInfo,
        }),
        z.object({
            data: CrewMerchantUi.claimListSchema,
            kind: z.literal(PrimaryEntity.Warranty),
            pageInfo,
        }),
        z.object({
            data: CrewMerchantUi.registrationListSchema,
            kind: z.literal(PrimaryEntity.Registration),
            pageInfo,
        }),
        z.object({
            data: storeProductList,
            kind: z.literal('Product'),
            pageInfo,
        }),
    ]);
    CrewMerchantUi.searchViewSchema = storeUiView;
    CrewMerchantUi.searchViewDateSchema = viewDateValue;
    CrewMerchantUi.reportFilters = reportFiltersByEntity;
    CrewMerchantUi.reportDefinition = reportDefinitionByEntity;
    CrewMerchantUi.reportInlineRun = reportInlineRunRequest;
    CrewMerchantUi.reportDefinitionCreate = reportDefinitionCreateRequest;
    CrewMerchantUi.reportDefinitionUpdate = reportDefinitionUpdateRequest;
    CrewMerchantUi.reportDefinitionResponseSchema = reportDefinitionResponse;
    CrewMerchantUi.storeAlertSchema = storeAlert;
    CrewMerchantUi.storeAlertUpdateSchema = storeAlertUpdate;
    CrewMerchantUi.storeAlertCreateSchema = storeAlertCreate;
    // ---------- Widget Ref ----------
    /** Identify reference integration the widget view is loaded within. */
    CrewMerchantUi.widgetRefSchema = z.object({
        ref: z.enum(['corso-concierge', 'kustomer', 'zendesk']),
    });
    // ---------- Token Auth ----------
    const tokenAuthBaseSchema = CrewMerchantUi.widgetRefSchema.safeExtend({
        email: z.email(),
    });
    const corsoConciergeTokenAuthSchema = tokenAuthBaseSchema.safeExtend({
        ref: z.literal('corso-concierge'),
        // provided as a query parameter by Front, which is the current integration context our agents use; https://dev.frontapp.com/docs/security#token-verification
        auth_secret: z.string().nonempty(),
    });
    const kustomerTokenAuthSchema = tokenAuthBaseSchema.safeExtend({
        ref: z.literal('kustomer'),
        // these are intended to be usable as a pattern for all integrations
        configId: z.coerce.number(),
        token: z.string().nonempty(),
    });
    // TODO consider better authentication, but that would likely require changes to leverage global OAuth with Zendesk
    const zendeskTokenAuthSchema = tokenAuthBaseSchema.safeExtend({
        ref: z.literal('zendesk'),
        /** `origin` provided a query parameter by Zendesk to the `iframe`d app */
        origin: z.url().regex(/^https?:\/\/(?<subdomain>.*)\.zendesk\.com/, {
            message: 'Invalid Zendesk Origin URL',
        }),
        installationId: z.string(),
    });
    /** Additional data for token authentication via query search parameters. */
    CrewMerchantUi.tokenAuthSchema = z.discriminatedUnion('ref', [
        // TODO assertion verifying that each ref is present
        corsoConciergeTokenAuthSchema,
        kustomerTokenAuthSchema,
        zendeskTokenAuthSchema,
    ]);
    // ---------- Kustomer ----------
    /** Extension beyond the base for anything Kustomer-specific. */
    CrewMerchantUi.kustomerCardSearchSchema = kustomerTokenAuthSchema.safeExtend({
        customerEmail: z.string(),
        customerId: z.string(),
        customerName: z.string(),
        appIdVersion: z.string(),
    });
    CrewMerchantUi.claimRiskAssessmentSchema = claimRiskAssessment;
    CrewMerchantUi.shippingClaimRiskAssessmentSchema = shippingClaimRiskAssessment;
    CrewMerchantUi.claimOrderLineageSchema = claimOrderLineage;
    // ---------- Store Discount Definitions ----------
    CrewMerchantUi.storeDiscountDefinitionSchema = storeDiscountDefinition;
    CrewMerchantUi.discountDefinitionSchema = discountDefinition;
    CrewMerchantUi.amountOffOrderRawSchema = amountOffOrderRaw;
    CrewMerchantUi.amountOffProductRawSchema = amountOffProductRaw;
})(CrewMerchantUi || (CrewMerchantUi = {}));
//# sourceMappingURL=schema.js.map